import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "ac-home" }
const _hoisted_2 = { class: "ac-home__head" }
const _hoisted_3 = { class: "ac-home__head-text" }
const _hoisted_4 = { class: "user-card__head" }
const _hoisted_5 = { class: "user-card__tags" }
const _hoisted_6 = { class: "blog-card__head" }
const _hoisted_7 = {
  key: 0,
  class: "masonry-container"
}
const _hoisted_8 = {
  key: 1,
  class: "masonry-container"
}
const _hoisted_9 = { class: "masonry-item__title" }

import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { Title, Card, Tag, Divider, Icon, Image, Skeleton, Typewriter } from 'animal-island-vue'
import request from '@/utils/request'
import { normalizeImageUrl } from '@/utils/imageUrl'
import userLogoSrc from '@/assets/user-logo/admin_logo.jpg'

interface BlogItem {
  title?: string
  image?: string | null
  [key: string]: any
}

/** el-tag 类型 → animal-island-vue Tag 颜色的映射表 */

export default /*@__PURE__*/_defineComponent({
  __name: 'HomePage',
  setup(__props) {

const router = useRouter()

/** 博客日志条目（后端 /guest/get_all_logs 返回结构） */
const tagColorMap: Record<string, string> = {
  danger: 'app-red',
  primary: 'app-blue',
  success: 'app-green',
  warning: 'app-yellow',
  info: 'brown',
}

/** 个人标签（登录后由 /user/select_user_msg 的 badge 字段覆盖） */
const userTagArr = ref<Array<{ type: string; text: string }>>([
  { type: 'danger', text: '共产主义者' },
  { type: 'primary', text: '二次元' },
  { type: 'success', text: '爱打篮球' },
  { type: 'primary', text: '电子游戏中度爱好者' },
  { type: 'danger', text: '做梦想当个作家' },
])

/** 个人时间线（登录后由 /user/select_user_msg 的 timeline 字段覆盖） */
const timelineArr = ref<Array<{ timestamp: string; content: string; type: string }>>([
  { timestamp: '2016-9', content: '邯郸学院嵌入式技术与应用专业入学', type: 'success' },
  { timestamp: '2019-05', content: '北京星震同源数字系统股份有限公司实习', type: 'success' },
  { timestamp: '2019-07', content: '北京星震同源数字系统股份有限公司实习结束', type: 'success' },
  { timestamp: '2019-7', content: '邯郸学院嵌入式技术与应用专业毕业', type: 'success' },
  { timestamp: '2019-9', content: '入职基成科技（天津）有限公司', type: 'success' },
  { timestamp: '2021-03', content: '入学河北工程大学计算机科学与技术专业', type: 'success' },
  { timestamp: '2023-07', content: '河北工程大学计算机科学与技术专业毕业', type: 'success' },
  { timestamp: '2023-10', content: '基成科技（天津）有限公司离职', type: 'primary' },
])

/** 博客日志列表 */
const blogArr = ref<BlogItem[]>([])
/** 瀑布流加载中（Skeleton 骨架占位） */
const blogsLoading = ref(true)

// 挂载时：校验登录态并拉取用户资料、博客日志（与原 mounted 逻辑一致）
onMounted(() => {
  loginStatus()
  getAllLogs()
})

/**
 * 函数功能：获取全部博客日志（瀑布流数据源，带加载态反馈）
 * 输入参数：无
 * 输出参数：无（成功后写入 blogArr，无论结果如何结束 blogsLoading）
 * 返回值：无
 */
function getAllLogs(): void {
  blogsLoading.value = true
  request
    .post('/guest/get_all_logs')
    .then((res: any) => {
      if (res.code == 200) {
        blogArr.value = Array.isArray(res.data) ? res.data : []
      }
    })
    .finally(() => {
      blogsLoading.value = false
    })
}

/**
 * 函数功能：点击博客卡片跳转到博客详情页（显式传递详情页所需字段，
 *           避免整对象传参时 null/对象值被 Vue Router 丢弃）
 * 输入参数：item - 被点击的博客对象（取 id/title/upload_time/text/group_name 传递）
 * 输出参数：无
 * 返回值：无
 */
function showThisBlog(item: BlogItem): void {
  router.push({
    name: 'blog_show',
    params: {
      id: item.id ?? 0,
      title: item.title ?? '',
      upload_time: item.upload_time ?? '',
      text: item.text ?? '',
      group_name: item.group_name ?? '',
    },
  })
}

/**
 * 函数功能：登录状态校验——读取 localStorage "honey-user"，已登录则拉取用户最新资料
 *           （badge 标签 / timeline 时间线覆盖默认值，数据库字段为空时回退
 *            localStorage 本地存档，与个人设置页保存联动），未登录跳转 /login
 * 输入参数：无
 * 输出参数：无（可能更新 userTagArr / timelineArr，或触发路由跳转）
 * 返回值：无
 */
function loginStatus(): void {
  const loginStatusString = localStorage.getItem('honey-user')
  if (loginStatusString != null) {
    const loginStatus = JSON.parse(loginStatusString)
    if (loginStatus) {
      const userId = loginStatus.id
      // 获取用户最新数据
      request.get('/user/select_user_msg/' + userId).then((res: any) => {
        if (res.code == 200) {
          const data = res.data
          // 数据库字段为空时回退本地存档（设置页保存时双写，后端落库前的联动兜底）
          const badge = data.badge || localStorage.getItem(`honey-user-badge-${userId}`) || ''
          const timeline =
            data.timeline || localStorage.getItem(`honey-user-timeline-${userId}`) || ''
          // 解析标签（颜色按索引稳定轮换，避免每次刷新随机变化）
          if (badge) {
            try {
              const tags = JSON.parse(badge)
              const types = ['danger', 'primary', 'success', 'warning', 'info']
              userTagArr.value = tags.map((tag: string, index: number) => ({
                type: types[index % types.length],
                text: tag,
              }))
            } catch (e) {
              console.log('标签解析失败', e)
            }
          }
          // 解析时间线
          if (timeline) {
            try {
              timelineArr.value = JSON.parse(timeline)
            } catch (e) {
              console.log('时间线解析失败', e)
            }
          }
        }
      })
    }
  } else {
    router.push('/login')
  }
}

return (_ctx: any,_cache: any) => {
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_timeline_item = _resolveComponent("el-timeline-item")!
  const _component_el_timeline = _resolveComponent("el-timeline")!
  const _component_el_aside = _resolveComponent("el-aside")!
  const _component_el_main = _resolveComponent("el-main")!
  const _component_el_container = _resolveComponent("el-container")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("header", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(_unref(Title), {
          size: "large",
          color: "app-orange"
        }, {
          default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
            _createTextVNode("🏝 我的岛屿小屋", -1)
          ]))]),
          _: 1
        }),
        _createVNode(_unref(Typewriter), { speed: 60 }, {
          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
            _createElementVNode("p", { class: "ac-home__welcome" }, " 欢迎回到动森小岛！这里记录着克利夫兰程序员的生活、思考与爱情故事 🌴 ", -1)
          ]))]),
          _: 1
        })
      ])
    ]),
    _createVNode(_unref(Divider), { type: "wave-yellow" }),
    _createVNode(_component_el_container, { class: "ac-home__body" }, {
      default: _withCtx(() => [
        _createVNode(_component_el_aside, {
          width: "330px",
          class: "ac-home__aside"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(Card), {
              color: "app-yellow",
              pattern: "default",
              class: "user-card"
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_4, [
                  _createVNode(_component_el_avatar, {
                    src: _unref(userLogoSrc),
                    size: 96,
                    class: "user-card__avatar"
                  }, null, 8, ["src"]),
                  _cache[3] || (_cache[3] = _createElementVNode("div", { class: "user-card__name" }, "克利夫兰程序员", -1))
                ]),
                _createVNode(_unref(Divider), { type: "dashed-brown" }),
                _createElementVNode("div", _hoisted_5, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(userTagArr.value, (item, index) => {
                    return (_openBlock(), _createBlock(_unref(Tag), {
                      key: index,
                      color: tagColorMap[item.type] || 'default',
                      size: "small"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(item.text), 1)
                      ]),
                      _: 2
                    }, 1032, ["color"]))
                  }), 128))
                ]),
                _createVNode(_unref(Divider), { type: "dashed-brown" }),
                _createVNode(_component_el_timeline, { class: "user-card__timeline" }, {
                  default: _withCtx(() => [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(timelineArr.value, (item, index) => {
                      return (_openBlock(), _createBlock(_component_el_timeline_item, {
                        key: index,
                        timestamp: item.timestamp,
                        type: item.type
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(item.content), 1)
                        ]),
                        _: 2
                      }, 1032, ["timestamp", "type"]))
                    }), 128))
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        }),
        _createVNode(_component_el_main, { class: "ac-home__main" }, {
          default: _withCtx(() => [
            _createVNode(_unref(Card), { class: "blog-card" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(Icon), {
                    name: "icon-camera",
                    size: 26,
                    bounce: ""
                  }),
                  _createVNode(_unref(Title), { color: "app-blue" }, {
                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                      _createTextVNode("最新日志", -1)
                    ]))]),
                    _: 1
                  })
                ]),
                (blogsLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      (_openBlock(), _createElementBlock(_Fragment, null, _renderList(6, (n) => {
                        return _createElementVNode("div", {
                          key: n,
                          class: "masonry-skeleton"
                        }, [
                          _createVNode(_unref(Skeleton), {
                            loading: true,
                            variant: "rect",
                            height: n % 3 === 0 ? 200 : 150
                          }, null, 8, ["height"]),
                          _createVNode(_unref(Skeleton), {
                            loading: true,
                            variant: "text",
                            width: "70%",
                            class: "masonry-skeleton__text"
                          })
                        ])
                      }), 64))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_8, [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(blogArr.value, (item, index) => {
                        return (_openBlock(), _createBlock(_unref(Card), {
                          key: index,
                          class: "masonry-item",
                          onClick: ($event: any) => (showThisBlog(item))
                        }, {
                          default: _withCtx(() => [
                            (item.image)
                              ? (_openBlock(), _createElementBlock("div", {
                                  key: 0,
                                  class: "masonry-item__pic",
                                  onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"]))
                                }, [
                                  _createVNode(_unref(Image), {
                                    src: _unref(normalizeImageUrl)(item.image),
                                    alt: item.title,
                                    width: "100%",
                                    lazy: ""
                                  }, null, 8, ["src", "alt"])
                                ]))
                              : _createCommentVNode("", true),
                            _createElementVNode("div", _hoisted_9, [
                              _createElementVNode("span", null, _toDisplayString(item.title), 1)
                            ])
                          ]),
                          _: 2
                        }, 1032, ["onClick"]))
                      }), 128))
                    ])),
                (!blogsLoading.value && blogArr.value.length === 0)
                  ? (_openBlock(), _createBlock(_unref(Card), {
                      key: 2,
                      type: "dashed",
                      class: "masonry-empty"
                    }, {
                      default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                        _createElementVNode("p", null, "🏝️ 岛屿日志正在搬运中，敬请期待…", -1)
                      ]))]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})